/**
 * ShippingManagement — unified admin oversight page for all shipping
 * providers (Terminal Africa + Chowdeck + future Shipbubble/Fez).
 *
 * Per project convention: ONE page for all providers, not one page per
 * provider. When a new provider is added, extend this file — do NOT
 * fork.
 *
 * Section A (this build):
 *   1. Providers Health  — Terminal + Chowdeck config, Terminal wallet
 *   2. Economics Config  — per-tier markup, per-tier daily cap,
 *                          minimum wallet balance
 *   3. Prohibited Items  — toggle defaults on/off, add custom keywords,
 *                          live preview
 *
 * Section B (next build): merchants, shipments, revenue rollup.
 *
 * Design language mirrors PaymentGatewayManagement — same header
 * layout, same tab pattern, same alert bar. Prefix `asm-` (Admin
 * Shipping Management) to isolate from admin theme.css.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Truck, Sliders, ShieldAlert, Activity, Wallet, Plus, Trash2, Check, X, RefreshCw,
  Users, Package, TrendingUp, ExternalLink,
} from 'lucide-react';
import './ShippingManagement.css';

const api = (path, opts = {}) => {
  return fetch('/api' + path, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
  });
};

// Canonical tier list — mirrors what the merchant tier field can hold.
// Tiers not in this list can still be typed manually into the config
// editor, but this is the primary set the UI presents.
const KNOWN_TIERS = ['FREE', 'PRO', 'PRO PLUS', 'PREMIUM', 'ENTERPRISE'];

function ShippingManagement() {
  const [activeTab, setActiveTab] = useState('health');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  // ── Providers health + revenue strip ────────────────────────────────
  const [providersStatus, setProvidersStatus] = useState(null);
  const [revenue, setRevenue] = useState(null);

  // ── Section B — merchants + shipments ───────────────────────────────
  const [merchants, setMerchants] = useState(null);
  const [merchantsProvider, setMerchantsProvider] = useState('all');
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [shipments, setShipments] = useState(null);
  const [shipmentsProvider, setShipmentsProvider] = useState('all');
  const [shipmentsStatus, setShipmentsStatus] = useState('');
  const [shipmentsLoading, setShipmentsLoading] = useState(false);

  // ── Economics config ────────────────────────────────────────────────
  const [config, setConfig] = useState(null);
  const [configBounds, setConfigBounds] = useState(null);
  const [configDraft, setConfigDraft] = useState(null); // editable copy
  const [configSaving, setConfigSaving] = useState(false);

  // ── Prohibited items ────────────────────────────────────────────────
  const [prohibited, setProhibited] = useState(null);
  const [prohibitedSaving, setProhibitedSaving] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  const [newCategory, setNewCategory] = useState('custom');
  const [newNote, setNewNote] = useState('');
  const [previewText, setPreviewText] = useState('');

  const flash = (setter, msg) => {
    setter(msg);
    setTimeout(() => setter(''), 4000);
  };

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [statusRes, configRes, prohibRes, revRes] = await Promise.all([
        api('/admin/shipping/providers-status'),
        api('/admin/shipping/config'),
        api('/admin/shipping/prohibited-items'),
        api('/admin/shipping/revenue?days=30'),
      ]);
      if (statusRes.ok) {
        const j = await statusRes.json();
        setProvidersStatus(j.providers);
      }
      if (configRes.ok) {
        const j = await configRes.json();
        setConfig(j.config);
        setConfigBounds(j.bounds);
        setConfigDraft(deepClone(j.config));
      }
      if (prohibRes.ok) {
        const j = await prohibRes.json();
        setProhibited(j);
      }
      if (revRes.ok) {
        const j = await revRes.json();
        setRevenue(j);
      }
    } catch (e) {
      setError('Failed to load shipping admin data.');
      console.error('[Shipping Admin] loadAll error:', e);
    } finally {
      setLoading(false);
    }
  };

  // ── Lazy-load merchants when tab opened ─────────────────────────────
  const loadMerchants = async (providerFilter = merchantsProvider) => {
    setMerchantsLoading(true);
    try {
      const res = await api(`/admin/shipping/merchants?provider=${encodeURIComponent(providerFilter)}&limit=50`);
      if (res.ok) setMerchants(await res.json());
    } catch (e) {
      setError('Failed to load merchants.');
    } finally {
      setMerchantsLoading(false);
    }
  };

  const loadShipments = async (providerFilter = shipmentsProvider, statusFilter = shipmentsStatus) => {
    setShipmentsLoading(true);
    try {
      const params = new URLSearchParams({ provider: providerFilter, limit: '50' });
      if (statusFilter) params.set('status', statusFilter);
      const res = await api(`/admin/shipping/shipments?${params.toString()}`);
      if (res.ok) setShipments(await res.json());
    } catch (e) {
      setError('Failed to load shipments.');
    } finally {
      setShipmentsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'merchants' && !merchants && !merchantsLoading) loadMerchants();
    if (activeTab === 'shipments' && !shipments && !shipmentsLoading) loadShipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => { loadAll(); }, []);

  // ═══════════════════════════════════════════════════════════════════
  //  Config editor helpers
  // ═══════════════════════════════════════════════════════════════════

  const updateDraftMarkupTier = (tier, value) => {
    setConfigDraft((d) => ({
      ...d,
      markup: {
        ...d.markup,
        byTier: value === '' || value === null
          ? Object.fromEntries(Object.entries(d.markup.byTier).filter(([k]) => k !== tier))
          : { ...d.markup.byTier, [tier]: Number(value) },
      },
    }));
  };
  const updateDraftMarkupDefault = (value) => {
    setConfigDraft((d) => ({
      ...d,
      markup: { ...d.markup, default: value === '' || value === null ? null : Number(value) },
    }));
  };
  const updateDraftCapTier = (tier, value) => {
    setConfigDraft((d) => ({
      ...d,
      dailyShipmentCap: {
        ...d.dailyShipmentCap,
        byTier: value === '' || value === null
          ? Object.fromEntries(Object.entries(d.dailyShipmentCap.byTier).filter(([k]) => k !== tier))
          : { ...d.dailyShipmentCap.byTier, [tier]: parseInt(value, 10) },
      },
    }));
  };
  const updateDraftCapDefault = (value) => {
    setConfigDraft((d) => ({
      ...d,
      dailyShipmentCap: { ...d.dailyShipmentCap, default: value === '' || value === null ? null : parseInt(value, 10) },
    }));
  };
  const updateDraftMinWallet = (value) => {
    setConfigDraft((d) => ({ ...d, minWalletBalance: value === '' || value === null ? null : Number(value) }));
  };

  const configDirty = useMemo(() => {
    if (!config || !configDraft) return false;
    return JSON.stringify(deepClone(config)) !== JSON.stringify(configDraft);
  }, [config, configDraft]);

  const saveConfig = async () => {
    setConfigSaving(true);
    setError('');
    try {
      const res = await api('/admin/shipping/config', {
        method: 'PUT',
        body: JSON.stringify({
          markup: configDraft.markup,
          dailyShipmentCap: configDraft.dailyShipmentCap,
          minWalletBalance: configDraft.minWalletBalance,
        }),
      });
      const j = await res.json();
      if (res.ok && j.success) {
        setConfig(j.config);
        setConfigDraft(deepClone(j.config));
        flash(setSuccess, 'Shipping economics updated.');
      } else {
        setError(j.error || 'Failed to save.');
      }
    } catch (e) {
      setError('Failed to save.');
    } finally {
      setConfigSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  //  Prohibited-items helpers
  // ═══════════════════════════════════════════════════════════════════

  const toggleDefaultKeyword = (keyword, currentlyEnabled) => {
    const disabled = new Set(prohibited.disabledDefaults || []);
    if (currentlyEnabled) disabled.add(keyword.toLowerCase());
    else disabled.delete(keyword.toLowerCase());
    saveProhibited({
      customAdditions: prohibited.customAdditions,
      disabledDefaults: [...disabled],
    });
  };

  const addCustom = () => {
    const kw = newKeyword.trim().toLowerCase();
    if (!kw) return;
    if (kw.length > 64) { setError('Keyword too long (max 64).'); return; }
    // Reject duplicates
    if ((prohibited.customAdditions || []).some((row) => row.keyword === kw)) {
      setError('That keyword is already in your custom list.'); return;
    }
    const next = {
      customAdditions: [
        ...(prohibited.customAdditions || []),
        { keyword: kw, category: newCategory || 'custom', note: newNote.trim() || undefined },
      ],
      disabledDefaults: prohibited.disabledDefaults || [],
    };
    saveProhibited(next);
    setNewKeyword(''); setNewNote('');
  };

  const removeCustom = (keyword) => {
    saveProhibited({
      customAdditions: (prohibited.customAdditions || []).filter((row) => row.keyword !== keyword),
      disabledDefaults: prohibited.disabledDefaults || [],
    });
  };

  const saveProhibited = async (body) => {
    setProhibitedSaving(true);
    setError('');
    try {
      const res = await api('/admin/shipping/prohibited-items', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (res.ok && j.success) {
        // Reload so defaults + enabled flags recompute correctly
        const fresh = await api('/admin/shipping/prohibited-items');
        if (fresh.ok) setProhibited(await fresh.json());
        flash(setSuccess, 'Prohibited items updated.');
      } else {
        setError(j.error || 'Failed to save.');
      }
    } catch (e) {
      setError('Failed to save.');
    } finally {
      setProhibitedSaving(false);
    }
  };

  // Live preview — matches sample text against effective blocklist
  const previewMatches = useMemo(() => {
    if (!previewText.trim() || !prohibited) return [];
    const haystack = previewText.toLowerCase();
    const disabled = new Set((prohibited.disabledDefaults || []).map((k) => k.toLowerCase()));
    const matches = [];
    for (const cat of prohibited.defaults) {
      for (const { keyword, enabled } of cat.keywords) {
        if (!enabled) continue;
        if (disabled.has(keyword.toLowerCase())) continue;
        if (haystack.includes(keyword.toLowerCase())) {
          matches.push({ keyword, category: cat.category, source: 'default' });
        }
      }
    }
    for (const row of prohibited.customAdditions || []) {
      if (haystack.includes(row.keyword.toLowerCase())) {
        matches.push({ keyword: row.keyword, category: row.category, source: 'custom' });
      }
    }
    return matches;
  }, [previewText, prohibited]);

  // ═══════════════════════════════════════════════════════════════════
  //  Render
  // ═══════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="asm-container">
        <div className="asm-loading"><RefreshCw size={20} className="asm-spin" /> Loading shipping admin…</div>
      </div>
    );
  }

  return (
    <div className="asm-container">
      <div className="asm-header">
        <h1><Truck size={28} /> Shipping</h1>
        <p>Provider health, per-tier economics, and prohibited-items screening for all shipping integrations.</p>
      </div>

      {error && <div className="asm-alert asm-alert-error"><X size={16} />{error}</div>}
      {success && <div className="asm-alert asm-alert-success"><Check size={16} />{success}</div>}

      <div className="asm-tabs">
        <button className={`asm-tab ${activeTab === 'health' ? 'active' : ''}`} onClick={() => setActiveTab('health')}>
          <Activity size={18} /> Health
        </button>
        <button className={`asm-tab ${activeTab === 'merchants' ? 'active' : ''}`} onClick={() => setActiveTab('merchants')}>
          <Users size={18} /> Merchants
        </button>
        <button className={`asm-tab ${activeTab === 'shipments' ? 'active' : ''}`} onClick={() => setActiveTab('shipments')}>
          <Package size={18} /> Shipments
        </button>
        <button className={`asm-tab ${activeTab === 'config' ? 'active' : ''}`} onClick={() => setActiveTab('config')}>
          <Sliders size={18} /> Economics
        </button>
        <button className={`asm-tab ${activeTab === 'prohibited' ? 'active' : ''}`} onClick={() => setActiveTab('prohibited')}>
          <ShieldAlert size={18} /> Prohibited items
        </button>
      </div>

      {/* ── HEALTH + revenue strip ─────────────────────────────────── */}
      {activeTab === 'health' && providersStatus && (
        <div className="asm-tab-content">
          {/* 30-day revenue strip — small stats cards at top */}
          {revenue && (
            <div className="asm-stat-strip">
              <div className="asm-stat-card">
                <TrendingUp size={20} />
                <div>
                  <p className="asm-stat-label">30-day shipping revenue</p>
                  <p className="asm-stat-value">₦{formatCurrency(revenue.total)}</p>
                  <p className="asm-stat-sub">{revenue.entriesCounted} shipments</p>
                </div>
              </div>
              {Object.entries(revenue.byProvider || {}).map(([source, amt]) => (
                <div className="asm-stat-card" key={source}>
                  <Truck size={20} />
                  <div>
                    <p className="asm-stat-label">{source.replace('_', ' ')}</p>
                    <p className="asm-stat-value">₦{formatCurrency(amt)}</p>
                    <p className="asm-stat-sub">markup captured</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="asm-provider-grid">
            <ProviderHealthCard
              title="Terminal Africa"
              status={providersStatus.terminal_africa}
              accent="teal"
              extra={providersStatus.terminal_africa.wallet && (
                <div className="asm-wallet">
                  <Wallet size={14} />
                  <span>Platform wallet: <strong>{formatCurrency(providersStatus.terminal_africa.wallet.balance)} {providersStatus.terminal_africa.wallet.currency}</strong></span>
                </div>
              )}
            />
            <ProviderHealthCard
              title="Chowdeck"
              status={providersStatus.chowdeck}
              accent="orange"
            />
          </div>

          {/* Tiny daily-revenue sparkline */}
          {revenue && revenue.byDay && revenue.byDay.length > 0 && (
            <div className="asm-card">
              <div className="asm-card-head">
                <h2>Daily shipping revenue — last {revenue.days} days</h2>
                <p className="asm-hint">Markup captured per dispatched shipment. Zero-days shown so gaps are visible.</p>
              </div>
              <div className="asm-sparkline">
                {(() => {
                  const max = Math.max(1, ...revenue.byDay.map((d) => d.amount));
                  return revenue.byDay.map((d) => (
                    <div key={d.date} className="asm-spark-bar" title={`${d.date} — ₦${formatCurrency(d.amount)}`}>
                      <div
                        className="asm-spark-fill"
                        style={{ height: `${Math.max(2, (d.amount / max) * 100)}%` }}
                      />
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MERCHANTS ──────────────────────────────────────────────── */}
      {activeTab === 'merchants' && (
        <div className="asm-tab-content">
          <div className="asm-toolbar">
            <label>
              Provider:
              <select
                className="asm-input"
                value={merchantsProvider}
                onChange={(e) => {
                  setMerchantsProvider(e.target.value);
                  loadMerchants(e.target.value);
                }}
              >
                <option value="all">All providers</option>
                <option value="terminal_africa">Terminal Africa</option>
                <option value="chowdeck">Chowdeck</option>
              </select>
            </label>
            <button className="asm-btn" onClick={() => loadMerchants()} disabled={merchantsLoading}>
              <RefreshCw size={14} className={merchantsLoading ? 'asm-spin' : ''} /> Refresh
            </button>
          </div>

          {merchantsLoading && !merchants ? (
            <div className="asm-loading"><RefreshCw size={16} className="asm-spin" /> Loading merchants…</div>
          ) : merchants && merchants.merchants.length === 0 ? (
            <p className="asm-empty">No merchants match this filter.</p>
          ) : merchants ? (
            <div className="asm-card">
              <table className="asm-list-table">
                <thead>
                  <tr>
                    <th>Merchant</th>
                    <th>Tier</th>
                    <th>Wallet available</th>
                    <th>Providers</th>
                    <th>Today (Terminal)</th>
                  </tr>
                </thead>
                <tbody>
                  {merchants.merchants.map((m) => (
                    <tr key={m.storeId}>
                      <td>
                        <div className="asm-merchant-cell">
                          <strong>{m.storeName || '—'}</strong>
                          <span className="asm-muted">{m.merchantEmail || m.merchantUsername || '—'}</span>
                        </div>
                      </td>
                      <td>{m.merchantTier || '—'}</td>
                      <td>
                        {m.wallet
                          ? <span className={m.wallet.availableBalance >= (config?.minWalletBalance || 0) ? '' : 'asm-wallet-low'}>
                              ₦{formatCurrency(m.wallet.availableBalance)}
                            </span>
                          : '—'}
                      </td>
                      <td>
                        <div className="asm-provider-chips">
                          {m.providers.terminal_africa.enabled && (
                            <span className="asm-chip asm-chip-default" style={{ background: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0' }}>Terminal</span>
                          )}
                          {m.providers.chowdeck.enabled && (
                            <span className="asm-chip asm-chip-custom" style={{ background: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' }}>Chowdeck</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {m.providers.terminal_africa.enabled ? (
                          <span>
                            {m.providers.terminal_africa.todayShipmentCount}
                            {m.providers.terminal_africa.dailyShipmentCap != null && (
                              <span className="asm-muted"> / {m.providers.terminal_africa.dailyShipmentCap}</span>
                            )}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {merchants.hasMore && (
                <p className="asm-audit-line">Showing first {merchants.limit} — pagination arrives with cursor support in a future pass.</p>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* ── SHIPMENTS ──────────────────────────────────────────────── */}
      {activeTab === 'shipments' && (
        <div className="asm-tab-content">
          <div className="asm-toolbar">
            <label>
              Provider:
              <select
                className="asm-input"
                value={shipmentsProvider}
                onChange={(e) => {
                  setShipmentsProvider(e.target.value);
                  loadShipments(e.target.value, shipmentsStatus);
                }}
              >
                <option value="all">All providers</option>
                <option value="terminal_africa">Terminal Africa</option>
                <option value="chowdeck">Chowdeck</option>
              </select>
            </label>
            <label>
              Status:
              <select
                className="asm-input"
                value={shipmentsStatus}
                onChange={(e) => {
                  setShipmentsStatus(e.target.value);
                  loadShipments(shipmentsProvider, e.target.value);
                }}
              >
                <option value="">Any</option>
                <option value="PROCESSING">Processing</option>
                <option value="SHIPPED">Shipped</option>
                <option value="DELIVERED">Delivered</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </label>
            <button className="asm-btn" onClick={() => loadShipments()} disabled={shipmentsLoading}>
              <RefreshCw size={14} className={shipmentsLoading ? 'asm-spin' : ''} /> Refresh
            </button>
          </div>

          {shipmentsLoading && !shipments ? (
            <div className="asm-loading"><RefreshCw size={16} className="asm-spin" /> Loading shipments…</div>
          ) : shipments && shipments.shipments.length === 0 ? (
            <p className="asm-empty">No shipments match this filter.</p>
          ) : shipments ? (
            <div className="asm-card">
              <table className="asm-list-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Merchant</th>
                    <th>Provider</th>
                    <th>Carrier</th>
                    <th>Tracking</th>
                    <th>Status</th>
                    <th>Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.shipments.map((s) => (
                    <tr key={s.orderId}>
                      <td><code>{s.orderNumber}</code></td>
                      <td className="asm-merchant-cell">
                        <strong>{s.merchant.storeName || '—'}</strong>
                        <span className="asm-muted">{s.merchant.email || '—'}</span>
                      </td>
                      <td>
                        <span className={`asm-provider-badge on`} style={{
                          background: s.provider === 'terminal_africa' ? '#ecfdf5' : '#fff7ed',
                          color: s.provider === 'terminal_africa' ? '#047857' : '#c2410c',
                          borderColor: s.provider === 'terminal_africa' ? '#a7f3d0' : '#fed7aa',
                        }}>
                          {s.provider === 'terminal_africa' ? 'Terminal' : 'Chowdeck'}
                        </span>
                      </td>
                      <td>{s.shipment?.carrierName || '—'}</td>
                      <td>
                        {s.shipment?.trackingNumber ? <code>{s.shipment.trackingNumber}</code> : '—'}
                        {s.shipment?.labelUrl && (
                          <a href={s.shipment.labelUrl} target="_blank" rel="noopener noreferrer" className="asm-icon-btn asm-inline-btn" title="Download waybill">
                            <ExternalLink size={12} />
                          </a>
                        )}
                      </td>
                      <td>
                        {s.shipment?.cancelled
                          ? <span className="asm-provider-badge off">Cancelled</span>
                          : <span>{s.orderStatus}</span>}
                      </td>
                      <td>₦{formatCurrency(s.shippingFee)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {shipments.hasMore && (
                <p className="asm-audit-line">Showing first {shipments.limit}.</p>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* ── ECONOMICS CONFIG ───────────────────────────────────────── */}
      {activeTab === 'config' && configDraft && configBounds && (
        <div className="asm-tab-content">
          <div className="asm-card">
            <div className="asm-card-head">
              <h2>Markup by tier</h2>
              <p className="asm-hint">Marketing lever — merchants pay less markup on higher tiers. Displayed at upgrade CTAs as "save X% shipping". Max {configBounds.maxMarkupPct}%.</p>
            </div>
            <table className="asm-config-table">
              <thead><tr><th>Tier</th><th>Markup %</th></tr></thead>
              <tbody>
                {KNOWN_TIERS.map((tier) => (
                  <tr key={tier}>
                    <td>{tier}</td>
                    <td>
                      <input
                        type="number" min="0" max={configBounds.maxMarkupPct} step="0.5"
                        placeholder="—"
                        value={configDraft.markup.byTier[tier] ?? ''}
                        onChange={(e) => updateDraftMarkupTier(tier, e.target.value)}
                        className="asm-input"
                      />
                    </td>
                  </tr>
                ))}
                <tr className="asm-config-default-row">
                  <td>Default (any other tier)</td>
                  <td>
                    <input
                      type="number" min="0" max={configBounds.maxMarkupPct} step="0.5"
                      placeholder="—"
                      value={configDraft.markup.default ?? ''}
                      onChange={(e) => updateDraftMarkupDefault(e.target.value)}
                      className="asm-input"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="asm-card">
            <div className="asm-card-head">
              <h2>Daily shipment cap by tier</h2>
              <p className="asm-hint">Runaway-shipment guard. Merchants CAN lower it further on their end but not raise. Max {configBounds.maxDailyCap.toLocaleString()}/day.</p>
            </div>
            <table className="asm-config-table">
              <thead><tr><th>Tier</th><th>Cap (shipments/day)</th></tr></thead>
              <tbody>
                {KNOWN_TIERS.map((tier) => (
                  <tr key={tier}>
                    <td>{tier}</td>
                    <td>
                      <input
                        type="number" min="0" max={configBounds.maxDailyCap} step="1"
                        placeholder="—"
                        value={configDraft.dailyShipmentCap.byTier[tier] ?? ''}
                        onChange={(e) => updateDraftCapTier(tier, e.target.value)}
                        className="asm-input"
                      />
                    </td>
                  </tr>
                ))}
                <tr className="asm-config-default-row">
                  <td>Default (any other tier)</td>
                  <td>
                    <input
                      type="number" min="0" max={configBounds.maxDailyCap} step="1"
                      placeholder="—"
                      value={configDraft.dailyShipmentCap.default ?? ''}
                      onChange={(e) => updateDraftCapDefault(e.target.value)}
                      className="asm-input"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="asm-card">
            <div className="asm-card-head">
              <h2>Minimum wallet balance</h2>
              <p className="asm-hint">Merchants must have at least this in their Revstar wallet to enable nationwide shipping. Max ₦{configBounds.maxMinWalletBalance.toLocaleString()}.</p>
            </div>
            <div className="asm-single-field">
              <span>₦</span>
              <input
                type="number" min="0" max={configBounds.maxMinWalletBalance} step="500"
                placeholder="—"
                value={configDraft.minWalletBalance ?? ''}
                onChange={(e) => updateDraftMinWallet(e.target.value)}
                className="asm-input"
              />
            </div>
          </div>

          <div className="asm-config-actions">
            <button className="asm-btn" onClick={() => setConfigDraft(deepClone(config))} disabled={!configDirty || configSaving}>Cancel</button>
            <button className="asm-btn asm-btn-primary" onClick={saveConfig} disabled={!configDirty || configSaving}>
              {configSaving ? 'Saving…' : 'Save changes'}
            </button>
          </div>

          {config?.updatedAt && (
            <p className="asm-audit-line">Last updated {new Date(config.updatedAt).toLocaleString()}{config.updatedBy ? ` by ${config.updatedBy}` : ''}.</p>
          )}
        </div>
      )}

      {/* ── PROHIBITED ITEMS ───────────────────────────────────────── */}
      {activeTab === 'prohibited' && prohibited && (
        <div className="asm-tab-content">

          {/* Live preview */}
          <div className="asm-card">
            <div className="asm-card-head">
              <h2>Live preview</h2>
              <p className="asm-hint">Paste any item name / description to see what would match the current effective blocklist.</p>
            </div>
            <textarea
              className="asm-preview-input"
              rows={2}
              placeholder="e.g. Ankara Ivory Necklace"
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
            />
            {previewText.trim() && (
              <div className="asm-preview-result">
                {previewMatches.length === 0
                  ? <span className="asm-preview-clean"><Check size={14} /> No matches — this would ship fine.</span>
                  : (
                    <>
                      <span className="asm-preview-bad"><ShieldAlert size={14} /> {previewMatches.length} match{previewMatches.length === 1 ? '' : 'es'}:</span>
                      <div className="asm-preview-matches">
                        {previewMatches.map((m, i) => (
                          <span key={i} className={`asm-chip asm-chip-${m.source}`}>
                            {m.keyword} <em>({m.category})</em>
                          </span>
                        ))}
                      </div>
                    </>
                  )}
              </div>
            )}
          </div>

          {/* Custom additions */}
          <div className="asm-card">
            <div className="asm-card-head">
              <h2>Custom additions</h2>
              <p className="asm-hint">Add keywords beyond the built-in defaults. Case-insensitive substring match on item names + descriptions.</p>
            </div>
            <div className="asm-add-row">
              <input
                className="asm-input"
                placeholder="keyword (e.g. lithium)"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
              />
              <input
                className="asm-input"
                placeholder="category (e.g. hazmat)"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
              />
              <input
                className="asm-input asm-input-wide"
                placeholder="internal note (optional)"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <button className="asm-btn asm-btn-primary" onClick={addCustom} disabled={prohibitedSaving || !newKeyword.trim()}>
                <Plus size={14} /> Add
              </button>
            </div>
            {(prohibited.customAdditions || []).length === 0 ? (
              <p className="asm-empty">No custom additions yet.</p>
            ) : (
              <table className="asm-list-table">
                <thead><tr><th>Keyword</th><th>Category</th><th>Note</th><th>Added</th><th></th></tr></thead>
                <tbody>
                  {prohibited.customAdditions.map((row) => (
                    <tr key={row.keyword}>
                      <td><code>{row.keyword}</code></td>
                      <td>{row.category}</td>
                      <td className="asm-muted">{row.note || '—'}</td>
                      <td className="asm-muted">{row.addedAt ? new Date(row.addedAt).toLocaleDateString() : '—'}</td>
                      <td>
                        <button className="asm-icon-btn" onClick={() => removeCustom(row.keyword)} disabled={prohibitedSaving} aria-label="Remove">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Default categories */}
          <div className="asm-card">
            <div className="asm-card-head">
              <h2>Default categories</h2>
              <p className="asm-hint">Built-in blocklist derived from major NG carrier restrictions. Toggle individual keywords off if they cause false positives for your merchants.</p>
            </div>
            {prohibited.defaults.map((cat) => (
              <div key={cat.category} className="asm-default-cat">
                <h3>{cat.category} <span className="asm-muted">({cat.keywords.length})</span></h3>
                <div className="asm-keyword-chips">
                  {cat.keywords.map(({ keyword, enabled }) => (
                    <button
                      key={keyword}
                      className={`asm-keyword-chip ${enabled ? 'on' : 'off'}`}
                      onClick={() => toggleDefaultKeyword(keyword, enabled)}
                      disabled={prohibitedSaving}
                      title={enabled ? 'Click to disable' : 'Click to re-enable'}
                    >
                      {enabled ? <Check size={12} /> : <X size={12} />} {keyword}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {prohibited.updatedAt && (
            <p className="asm-audit-line">Last updated {new Date(prohibited.updatedAt).toLocaleString()}{prohibited.updatedBy ? ` by ${prohibited.updatedBy}` : ''}.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Small presentational bits ─────────────────────────────────────────

function ProviderHealthCard({ title, status, accent, extra }) {
  const configured = Boolean(status?.configured);
  return (
    <div className={`asm-provider-card asm-accent-${accent}`}>
      <div className="asm-provider-head">
        <h3>{title}</h3>
        <span className={`asm-provider-badge ${configured ? 'on' : 'off'}`}>
          {configured ? 'Configured' : 'Not configured'}
        </span>
      </div>
      {status?.sandbox && <span className="asm-provider-mode">Sandbox</span>}
      {!configured && status?.missing?.length > 0 && (
        <p className="asm-provider-missing">Missing: {status.missing.join(', ')}</p>
      )}
      {extra}
    </div>
  );
}

function deepClone(o) { return JSON.parse(JSON.stringify(o)); }

function formatCurrency(n) {
  if (n == null) return '—';
  try { return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  catch { return String(n); }
}

export default ShippingManagement;
